import { useEffect, useRef, useState } from 'react';
import { NavBarBreakpointItem } from '~/services/layout/types';

type NavBarBreakpointStyle = React.CSSProperties & {
  [key in `--${string}`]?: string;
};

export type NavBarBreakpointScrollTransitionStyles = {
  background_color?: string;
  top_gradient_color?: string;
  bottom_gradient_color?: string;
  border_color?: string;
  shadow_switch?: boolean;
  x_offset?: number;
  y_offset?: number;
  blur?: number;
  spread?: number;
  color?: string;
  background_blur_switch?: boolean;
  background_blur?: number;
  scroll_transition_switch?: boolean;
  scroll_trigger?: number;
  change_background_color_switch?: boolean;
  new_background_color?: string;
  new_top_gradient_color?: string;
  new_bottom_gradient_color?: string;
  change_border_color_switch?: boolean;
  new_border_color?: string;
  change_background_blur_switch?: boolean;
  new_background_blur?: number;
  change_shadow_color_switch?: boolean;
  new_shadow_color?: string;
  transition_duration?: number | string;
  transition_easing?: string;
};

export function useNavbarBreakpointScrollTransition({
  styles,
  navItem,
}: {
  styles: NavBarBreakpointScrollTransitionStyles;
  navItem: NavBarBreakpointItem;
}): {
  scrollTransitionStyles: NavBarBreakpointStyle;
} {
  const [isScrollTriggered, setIsScrollTriggered] = useState<boolean>(false);

  const isScrollTriggeredRef = useRef<boolean>(false);

  const {
    background_color,
    top_gradient_color,
    bottom_gradient_color,
    border_color,
    shadow_switch,
    x_offset,
    y_offset,
    blur,
    spread,
    color,
    background_blur_switch,
    background_blur,
    scroll_transition_switch,
    scroll_trigger,
    change_background_color_switch,
    new_background_color,
    new_top_gradient_color,
    new_bottom_gradient_color,
    change_border_color_switch,
    new_border_color,
    change_background_blur_switch,
    new_background_blur,
    change_shadow_color_switch,
    new_shadow_color,
    transition_duration,
    transition_easing,
  } = styles;

  useEffect(() => {
    isScrollTriggeredRef.current = isScrollTriggered;
  }, [isScrollTriggered]);

  useEffect(() => {
    if (!navItem || !scroll_transition_switch) return;

    const trigger: number = Number(scroll_trigger) || 0;

    const handleScroll = () => {
      const nextTriggered: boolean = window.scrollY >= trigger;

      if (nextTriggered === isScrollTriggeredRef.current) return;

      isScrollTriggeredRef.current = nextTriggered;

      setIsScrollTriggered(nextTriggered);
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [scroll_transition_switch, scroll_trigger]);

  const shouldApplyScrollTransition: boolean = Boolean(
    scroll_transition_switch
  );

  if (!navItem || !shouldApplyScrollTransition)
    return { scrollTransitionStyles: {} };

  const shouldApplyTransitionValues: boolean =
    shouldApplyScrollTransition && isScrollTriggered;

  const activeBackgroundColor: string | undefined =
    shouldApplyTransitionValues && change_background_color_switch
      ? new_background_color
      : background_color;

  const activeTopGradientColor: string | undefined = shouldApplyTransitionValues
    ? new_top_gradient_color
    : top_gradient_color;

  const activeBottomGradientColor: string | undefined =
    shouldApplyTransitionValues
      ? new_bottom_gradient_color
      : bottom_gradient_color;

  const activeBorderColor: string | undefined =
    shouldApplyTransitionValues && change_border_color_switch
      ? new_border_color
      : border_color;

  const activeShadowColor: string | undefined =
    shouldApplyTransitionValues && change_shadow_color_switch
      ? new_shadow_color
      : color;

  const activeBackgroundBlur: number =
    shouldApplyTransitionValues && change_background_blur_switch
      ? Number(new_background_blur) || 0
      : Number(background_blur) || 0;

  const shouldShowBackgroundBlur: boolean = Boolean(background_blur_switch);
  const shouldShowShadow: boolean = Boolean(shadow_switch);

  const transitionDuration: string = getTransitionDuration(transition_duration);
  const transitionTimingFunction: string = transition_easing || 'ease-in-out';

  const scrollTransitionStyles: NavBarBreakpointStyle = {
    '--nav_bar_breakpoint_background_color': activeBackgroundColor,
    '--nav_bar_breakpoint_top_gradient_color': activeTopGradientColor,
    '--nav_bar_breakpoint_bottom_gradient_color': activeBottomGradientColor,
    '--nav_bar_breakpoint_border_color': activeBorderColor,
    boxShadow: shouldShowShadow
      ? `${Number(x_offset) || 0}px ${Number(y_offset) || 0}px ${
          Number(blur) || 0
        }px ${Number(spread) || 0}px ${activeShadowColor || 'transparent'}`
      : undefined,
    backdropFilter: shouldShowBackgroundBlur
      ? `blur(${activeBackgroundBlur}px)`
      : undefined,
    WebkitBackdropFilter: shouldShowBackgroundBlur
      ? `blur(${activeBackgroundBlur}px)`
      : undefined,
    transitionProperty:
      'background-color, border-color, box-shadow, backdrop-filter, -webkit-backdrop-filter, --nav_bar_breakpoint_top_gradient_color, --nav_bar_breakpoint_bottom_gradient_color',
    transitionDuration,
    transitionTimingFunction,
  };

  return { scrollTransitionStyles };
}

function getTransitionDuration(duration?: number | string): string {
  const parsedDuration: number = Number(duration);

  if (Number.isNaN(parsedDuration) || parsedDuration <= 0) {
    return '0s';
  }

  return `${parsedDuration}s`;
}
